import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import AppBar from "../../common/AppBar";
import {
  createSuggestionRule,
  updateSuggestionRule,
} from "../../redux/suggestionSlice";
import { getAllTypes } from "../../redux/typeSlice";
import TypesPopup from "../../common/AppSpecificConpnents/Types/TypesPopup";
import ExportCsvBtn from "../../common/AppSpecificConpnents/ExportCsvBtn";
import { exportSuggestionRulesCSV } from "../../redux/suggestionSlice";
import "../../common/FormFields/FormFields.css";
import "./SuggestionRulesForm.css";
import InputWithPen from "../../common/Inputs/InputWithPen.js";
import { buildPermissionContext } from "../../util/permissions";
const RULE_NUMBER_OPTIONS = [
  "None",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
];

const SuggestionRuleForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isEditMode = location.state?.editMode || false;
  const existingRule = location.state?.suggestionRule || {};

  const { types, loading } = useSelector((state) => state.type);
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  const [suggestionData, setSuggestionData] = useState({
    id: existingRule?.rule_id || existingRule?.id || null,
    name: existingRule?.rule_name || "",
    suggestion_type_ref:
      existingRule?.suggestion_type_ref || existingRule?.type_id_ref || "",
  });
  const [typesModalOpen, setTypesModalOpen] = useState(false);
  const [selectedTypeName, setSelectedTypeName] = useState("");
  const [columnRule, setColumnRule] = useState([3, 4]);
  const [rowRule, setRowRule] = useState([9, 10]);
  const lastRuleNumber = RULE_NUMBER_OPTIONS[RULE_NUMBER_OPTIONS.length - 1];

  useEffect(() => {
    dispatch(getAllTypes({ limit: 100, offset: 0 })); // Fetch available types

    if (isEditMode) {
      setSuggestionData((prev) => ({
        ...prev,
        id: existingRule?.rule_id || existingRule?.id || null,
        name: existingRule?.rule_name || "",
        suggestion_type_ref:
          existingRule?.suggestion_type_ref || existingRule?.type_id_ref || "",
      }));
    }
    // Only include `isEditMode` in dependencies to avoid infinite loop
  }, [dispatch, isEditMode]);

  // Keep a readable type name alongside the selected id
  useEffect(() => {
    if (suggestionData.suggestion_type_ref && Array.isArray(types)) {
      const t = types.find(
        (ty) => ty.type_id === suggestionData.suggestion_type_ref
      );
      if (t) setSelectedTypeName(t.name);
    }
  }, [types, suggestionData.suggestion_type_ref]);

  const handleChange = (e) => {
    setSuggestionData({ ...suggestionData, [e.target.name]: e.target.value });
  };

  const handleSelectType = (type) => {
    setSuggestionData((prev) => ({
      ...prev,
      suggestion_type_ref: type.type_id,
    }));
    setSelectedTypeName(type.name);
    setTypesModalOpen(false);
  };

  const handleColumnRuleChange = (value) => {
    if (value === "None") {
      setColumnRule([]);
    } else {
      const numValue = parseInt(value);
      if (columnRule.includes(numValue)) {
        setColumnRule(columnRule.filter((item) => item !== numValue));
      } else {
        setColumnRule([...columnRule, numValue].sort((a, b) => a - b));
      }
    }
  };

  const handleRowRuleChange = (value) => {
    if (value === "None") {
      setRowRule([]);
    } else {
      const numValue = parseInt(value);
      if (rowRule.includes(numValue)) {
        setRowRule(rowRule.filter((item) => item !== numValue));
      } else {
        setRowRule([...rowRule, numValue].sort((a, b) => a - b));
      }
    }
  };

  // Fetch IP address and generate hash
  useEffect(() => {}, []);

  const handleSave = async () => {
    let ip = "";

    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      ip = data.ip;
    } catch (err) {
      console.error("Failed to fetch IP:", err);
    }

    const audit_trail_data = {
      user_ref: localStorage.getItem("user_id") || "",
      action: isEditMode ? "Update Suggestion Rule" : "Create Suggestion Rule",
      comment: isEditMode
        ? `Updated suggestion rule: ${suggestionData.name}`
        : `Created suggestion rule: ${suggestionData.name}`,
      date: new Date().toISOString(),
      ip_address: ip, // Now correctly assigned
      device_info: navigator.userAgent,
      signature: "some_signature",
      parent_version: isEditMode ? existingRule.version_id_ref : null,
      version_content: isEditMode
        ? {
            previous_values: {
              id: existingRule.id,
              name: existingRule.name,
              suggestion_type_ref: existingRule.suggestion_type_ref,
            },
            updated_values: {
              id: suggestionData.id,
              name: suggestionData.name,
              suggestion_type_ref: suggestionData.suggestion_type_ref,
            },
          }
        : {
            new_values: {
              id: suggestionData.id,
              name: suggestionData.name,
              suggestion_type_ref: suggestionData.suggestion_type_ref,
            },
          },
    };

    const payload = {
      rule_name: suggestionData.name,
      column_rule_indices: columnRule,
      row_rule_indices: rowRule,
      suggestion_type_ref: suggestionData.suggestion_type_ref,
      suggestion_cross_reference_table: "tbl_suggestion_rules",
      audit_trail_data,
    };

    if (isEditMode) {
      dispatch(updateSuggestionRule({ id: suggestionData.id, data: payload }));
    } else {
      dispatch(createSuggestionRule(payload));
    }

    navigate("/suggestionRules");
  };

  return (
    <div>
      <AppBar
        title={isEditMode ? "Edit Suggestion Rule" : "New Suggestion Rule"}
        buttons={[
          {
            label: "Save",
            onClick: canEdit ? handleSave : undefined,
            disabled: !canEdit,
            title: !canEdit
              ? "You do not have permission to save suggestion rules."
              : undefined,
          },
        ]}
      />
      <form>
        <div className="form-group">
          <label>Suggestion Rule Name*</label>
          <input
            type="text"
            name="name"
            value={suggestionData.name}
            onChange={handleChange}
            className="form-text-field form-field-width-20"
          />
        </div>

        <div className="form-group">
          <div style={{ width: "20%" }}>
            <InputWithPen
              label="Part Type"
              required={true}
              placeholder="Select Type"
              val={selectedTypeName}
              onClickOnPencil={() => setTypesModalOpen(true)}
              onClear={() => {
                setSelectedTypeName("");
                setSuggestionData((prev) => ({
                  ...prev,
                  suggestion_type_ref: "",
                }));
              }}
            />
          </div>
        </div>

        {/* Suggestion Cross Reference Table Section */}
        <div className="form-group">
          <label>Suggestion Cross Reference Table*</label>
          <div style={{ marginBottom: "10px" }}>
            <span style={{ color: "#666", fontSize: "14px" }}>
              Click Upload CSV for the format
            </span>
          </div>
          <div style={{ textAlign: "right" }}>
            <ExportCsvBtn
              buttonLabel="Export CSV"
              handleExportCSV={() => dispatch(exportSuggestionRulesCSV())}
              whiteVariant
            />
            <button
              className="appbar-button"
              style={{
                border: "1px solid #0D6EFD",
                backgroundColor: "#0D6EFD",
                color: "#ffffff",
                marginLeft: "10px",
              }}
              onClick={() =>
                document.getElementById("suggRuleCsvFile")?.click()
              }
            >
              Upload CSV
            </button>
            <input
              id="suggRuleCsvFile"
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={() => {}}
            />
          </div>
        </div>

        {/* Rule Configuration Section */}
        <div className="form-group">
          <label>Rule Configuration</label>

          <div className="rule-config-grid">
            <div className="rule-config-label">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="8" width="2" height="6" fill="#666666" />
                <rect x="6" y="5" width="2" height="9" fill="#666666" />
                <rect x="10" y="2" width="2" height="12" fill="#666666" />
                <rect x="14" y="7" width="2" height="7" fill="#666666" />
              </svg>
              <span>Column Rule</span>
            </div>
            <div className="rule-config-numbers rule-config-numbers--column">
              <div className="rule-config-chip-list">
                {RULE_NUMBER_OPTIONS.map((num) => {
                  const isActive =
                    num !== "None" && columnRule.includes(parseInt(num, 10));
                  const isFirstNumber = num === "1";
                  const isLastNumber = num === lastRuleNumber;
                  return (
                    <button
                      key={num}
                      type="button"
                      className={`rule-config-chip${
                        isActive ? " rule-config-chip--active" : ""
                      }${
                        isFirstNumber ? " rule-config-chip--first-number" : ""
                      }${isLastNumber ? " rule-config-chip--last-number" : ""}`}
                      onClick={() => handleColumnRuleChange(num)}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rule-config-spacer" />

            <div className="rule-config-connectors">
              <div className="rule-config-connector-button rule-config-connector-button--add">
                <button
                  type="button"
                  onClick={() => {
                    const nextCol = Math.max(...columnRule, 0) + 1;
                    const nextRow = Math.max(...rowRule, 0) + 1;
                    if (nextCol <= 10) {
                      setColumnRule(
                        [...columnRule, nextCol].filter((x) => x <= 10)
                      );
                    }
                    if (nextRow <= 10) {
                      setRowRule([...rowRule, nextRow].filter((x) => x <= 10));
                    }
                  }}
                >
                  -
                </button>
              </div>
              <div className="rule-config-connector-button rule-config-connector-button--subtract">
                <button
                  type="button"
                  onClick={() => {
                    setColumnRule([]);
                    setRowRule([]);
                  }}
                >
                  +
                </button>
              </div>
            </div>

            <div className="rule-config-label">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="4" width="12" height="1.5" fill="#666666" />
                <rect x="2" y="7.5" width="12" height="1.5" fill="#666666" />
                <rect x="2" y="11" width="12" height="1.5" fill="#666666" />
              </svg>
              <span>Row Rule</span>
            </div>
            <div className="rule-config-numbers rule-config-numbers--row">
              <div className="rule-config-chip-list">
                {RULE_NUMBER_OPTIONS.map((num) => {
                  const isActive =
                    num !== "None" && rowRule.includes(parseInt(num, 10));
                  const isFirstNumber = num === "1";
                  const isLastNumber = num === lastRuleNumber;
                  return (
                    <button
                      key={num}
                      type="button"
                      className={`rule-config-chip${
                        isActive ? " rule-config-chip--active" : ""
                      }${
                        isFirstNumber ? " rule-config-chip--first-number" : ""
                      }${isLastNumber ? " rule-config-chip--last-number" : ""}`}
                      onClick={() => handleRowRuleChange(num)}
                    >
                      {num}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#666",
              textAlign: "left",
              marginTop: "0",
              marginLeft: "300px",
            }}
          >
            Select Characters to be matched against Suggestion Format Table Rows
          </div>
        </div>
      </form>

      {typesModalOpen && (
        <TypesPopup
          closeTypesModal={() => setTypesModalOpen(false)}
          onSelectType={handleSelectType}
        />
      )}
    </div>
  );
};

export default SuggestionRuleForm;
